/**
 * +rogue-specs: bot kits for the Rogue specialisations (docs/design/classes/specs.md "Rogue", the
 * rotation sections). The APL presses evolved ids directly (the spec'd ClassDef binds them); key 1
 * resolves to the opener while the stealth buff's override is up.
 */
import { MIRAGE, MIRAGE_AURA, SHADOWDANCER, SHADOWDANCER_AURA, TWO_TOXINS, VENOMWRIGHT, VENOMWRIGHT_AURA } from '@keystone/content';
import type { ClassKit } from '../kits';
import type { AplCond } from '../apl';

const SD = SHADOWDANCER;
const SDA = SHADOWDANCER_AURA;
const M = MIRAGE;
const MA = MIRAGE_AURA;
const V = VENOMWRIGHT;
const VA = VENOMWRIGHT_AURA;

const edge = (n: number): AplCond => ({ type: 'resource', resource: 'edge', atLeast: n });
const tempo = (n: number): AplCond => ({ type: 'resource', resource: 'tempo', atLeast: n });
/** A fight that will last (a boss, a dungeon pack, a dummy): the target has at least your own maximum health left. A field mob doesn't. */
const LASTING: AplCond = { type: 'targetHealthAbove', selfMaxHp: 1 };
const sil = (n: number): AplCond => ({ type: 'selfAura', auraId: SDA.silhouettes, minStacks: n });
const noSil = (n: number): AplCond => ({ type: 'not', cond: sil(n) });

/**
 * Shadowdancer: Duskwalk → Gloamstrike to open (and again mid-fight), keep Umbral Step charges
 * rolling so Silhouettes stand behind you, Umbral Finale at 4+ Edge with 2–3 Silhouettes (or 5 Edge),
 * Convergence + Veil of Night for the burst, Shadow Fan in packs.
 */
export const shadowdancerKit: ClassKit = {
  classId: 'rogue',
  role: 'dps',
  ranged: false,
  interrupt: SD.shadeJab,
  personalDefensive: SD.veilOfNight,
  oocBuffs: [{ ability: SD.duskwalk, when: [{ type: 'selfLacksAura', auraId: SDA.duskwalk }, { type: 'targetDistBelow', yd: 35 }] }],
  apl: [
    // The opener (key 1 while Duskwalking).
    { ability: SD.twinFang, when: [{ type: 'any', of: [{ type: 'selfAura', auraId: SDA.duskwalk }, { type: 'selfAura', auraId: SDA.duskwalkFight }] }] },
    { ability: SD.umbralStep, when: [{ type: 'targetDistAbove', yd: 9 }] },
    // Burst: Convergence on a lasting target or a pack.
    { ability: SD.umbralConvergence, when: [{ type: 'inCombat' }, edge(3), { type: 'any', of: [{ type: 'targetHpAbove', pct: 0.4 }, { type: 'enemiesNearSelf', atLeast: 3, radius: 7 }] }] },
    // Veil of Night: the defensive.
    { ability: SD.veilOfNight, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.45 }] },
    // (Bots keep Veil for defence; a player can also spend it on Convergence for the guaranteed crit.)
    // Duskwalk mid-fight while Edge is low: Gloamstrike's 2 Edge + a Silhouette.
    // (Only into a fight that will last: on a field mob it would cost the next pull's opener.)
    { ability: SD.duskwalk, when: [{ type: 'inCombat' }, { type: 'not', cond: edge(3) }, LASTING] },
    // Step for a Silhouette before cashing, or before a charge caps.
    { ability: SD.umbralStep, when: [edge(3), noSil(2)] },
    { ability: SD.umbralStep, when: [{ type: 'cooldownBelow', ability: SD.umbralStep, ms: 1500, nextCharge: true }, noSil(3)] },
    // Cash: 2+ Silhouettes, or 5 Edge (don't waste), or the Convergence window.
    // (At 4 Edge with fewer than 2 clones and no Step ready: one more Twin Fang to 5 first.)
    { ability: SD.umbralFinale, when: [edge(4), { type: 'any', of: [sil(2), edge(5), { type: 'selfAura', auraId: SDA.convergence }] }] },
    // In a pack, pool Tempo for Shadow Fan (every clone fans with you).
    { ability: SD.shadowFan, when: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 7 }, tempo(35)] },
    { ability: SD.twinFang, when: [tempo(25), { type: 'not', cond: { type: 'enemiesNearSelf', atLeast: 3, radius: 7 } }] },
    { ability: SD.twinFang, when: [tempo(25), edge(3), { type: 'not', cond: edge(4) }] },
  ],
};

/**
 * Mirage: keep 3 Phantoms with Falsestep (the threat builder), Return Blow at 3 Edge (free after a
 * Perfect Sidestep), Sidestep on cooldown into the swings, Mirror Spin in packs, Haze and Hall of
 * Mirrors on big pulls or when it hurts.
 */
export const mirageKit: ClassKit = {
  classId: 'rogue',
  role: 'tank',
  ranged: false,
  interrupt: M.mirrorJab,
  taunt: M.phantomLure,
  majorDefensive: M.haze,
  activeMitigation: M.sidestep,
  activeMitigationAura: MA.sidestepWindow,
  apl: [
    { ability: M.phantomLure, when: [{ type: 'targetDistAbove', yd: 9 }] },
    { ability: M.hallOfMirrors, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 8 }, { type: 'selfHpBelow', pct: 0.45 }] }] },
    { ability: M.haze, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 8 }, { type: 'selfHpBelow', pct: 0.55 }] }] },
    // A free counter is always worth it.
    { ability: M.returnBlow, when: [{ type: 'selfAura', auraId: MA.counterpoise }] },
    { ability: M.sidestep, when: [{ type: 'inCombat' }, { type: 'targetDistBelow', yd: 6 }] },
    { ability: M.returnBlow, when: [edge(3), { type: 'any', of: [{ type: 'selfAura', auraId: MA.phantoms, minStacks: 2 }, edge(5), { type: 'selfHpBelow', pct: 0.6 }] }] },
    { ability: M.mirrorSpin, when: [{ type: 'enemiesNearSelf', atLeast: 2, radius: 7 }, tempo(30)] },
    { ability: M.falsestep, when: [tempo(20)] },
    // Top up Phantoms with the taunt when Tempo runs dry.
    { ability: M.phantomLure, when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'selfAura', auraId: MA.phantoms, minStacks: 3 } }] },
    { ability: M.returnBlow, when: [edge(3)] },
  ],
};

/** No unpulled enemy would be splashed: alone, or the pack around the target is already engaged. */
const noSplash = (radius: number): AplCond => ({ type: 'any', of: [{ type: 'not', cond: { type: 'packNearTarget', atLeast: 2, radius } }, { type: 'targets', atLeast: 2, radius }] });

const twoKinds: AplCond = {
  type: 'any',
  of: [
    { type: 'all', of: [{ type: 'targetAura', auraId: VA.corrode }, { type: 'targetAura', auraId: VA.blight }] },
    { type: 'all', of: [{ type: 'targetAura', auraId: VA.corrode }, { type: 'targetAura', auraId: VA.numb }] },
    { type: 'all', of: [{ type: 'targetAura', auraId: VA.numb }, { type: 'targetAura', auraId: VA.blight }] },
  ],
};
void TWO_TOXINS;

/** Sneaking up in Creeping Fume: never break it from range (a vial would pull before the Paralytic Opener). */
const notFumed: AplCond = { type: 'not', cond: { type: 'selfAura', auraId: VA.fume } };

/**
 * Venomwright: Creeping Fume → Paralytic Opener (Numb), Venom Edge for Corrode, Flask Toss to keep
 * Blight up (a second kind) and on packs, Caustic Roll and Plaguebloom on cooldown in a fight,
 * Catalyst at 4 Edge with two kinds on the target (the reaction), or at 5 Edge regardless. Never
 * tosses a vial out of its own fume (the opener lands first).
 */
export const venomwrightKit: ClassKit = {
  classId: 'rogue',
  role: 'dps',
  ranged: false,
  interrupt: V.numbingJab,
  personalDefensive: V.chokingGas,
  oocBuffs: [{ ability: V.creepingFume, when: [{ type: 'selfLacksAura', auraId: VA.fume }, { type: 'targetDistBelow', yd: 35 }] }],
  apl: [
    { ability: V.venomEdge, when: [{ type: 'any', of: [{ type: 'selfAura', auraId: VA.fume }, { type: 'selfAura', auraId: VA.fumeFight }] }] },
    { ability: V.causticRoll, when: [{ type: 'targetDistAbove', yd: 9 }] },
    { ability: V.chokingGas, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.45 }] },
    // Plaguebloom on an engaged pack, or on a lone target (never next to a camp you haven't pulled).
    { ability: V.plaguebloom, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'targets', atLeast: 3, radius: 8 }, { type: 'all', of: [{ type: 'targetHpAbove', pct: 0.4 }, noSplash(10)] }] }] },
    // Fume mid-fight only into a fight that will last: on a field mob it would cost the next pull's opener.
    { ability: V.creepingFume, when: [{ type: 'inCombat' }, { type: 'not', cond: edge(3) }, LASTING] },
    { ability: V.causticRoll, when: [{ type: 'inCombat' }, { type: 'targetDistBelow', yd: 6 }] },
    { ability: V.catalyst, when: [edge(4), { type: 'any', of: [twoKinds, edge(5)] }] },
    { ability: V.flaskToss, when: [{ type: 'selfAura', auraId: VA.volatileFumes }, noSplash(7), notFumed] },
    // Blight: on every pack, and kept up on a single target as the second toxin kind.
    { ability: V.flaskToss, when: [{ type: 'targets', atLeast: 3, radius: 6 }, tempo(35), { type: 'targetNeedsAura', auraId: VA.blight, refreshBelowMs: 4000 }, notFumed] },
    { ability: V.flaskToss, when: [tempo(35), { type: 'not', cond: { type: 'targetAura', auraId: VA.blight } }, noSplash(7), notFumed] },
    { ability: V.flaskToss, when: [{ type: 'targets', atLeast: 3, radius: 6 }, tempo(35), notFumed] },
    { ability: V.venomEdge, when: [tempo(25)] },
  ],
};

/** Spec id → kit. */
export const ROGUE_SPEC_KITS: Readonly<Record<string, ClassKit>> = { shadowdancer: shadowdancerKit, mirage: mirageKit, venomwright: venomwrightKit };
